import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { login as apiLogin, getMe, verifyTotp, logoutSession } from '../services/api'
import api from '../services/api'
import { getToken, setToken, clearToken, migrateSessionTokenIfElectron, consumeAuthHashIfPresent } from '../services/token'
import { setUser as setSentryUser, clearUser as clearSentryUser } from '../lib/sentry'
import { resetReminderState } from '../hooks/useReminders'
import { useAsk } from './PromptProvider'
import { useToast } from './ToastContext'
import timerStore, { MESSAGES, tokenUid } from '../lib/timerStore'
import { claimQuickListsFor } from '../lib/quickListOwner'
import { claimDisplaySizeFor } from '../lib/displayZoom'

const AuthContext = createContext(null)

// Sign-out ends the server session first (POST /api/auth/logout, via
// services/api.js logoutSession), so a copied token stops working at once.
// It is best-effort: whatever the server does — fails, 401s, hangs — this
// device signs out anyway, and never waits longer than this.  logoutSession
// is bounded at ~2 s itself; this is the independent backstop.
//
// Resolves true when nothing is left alive on the server: it ended the
// session (ok), the session had already ended (401), or nobody was signed in
// (skipped).  false when that is unconfirmed — no answer, a 5xx/503, a throw,
// or the wait ran out — and logout() then leaves a one-time notice for the
// next page (UnconfirmedSignOutNotice), because copies of the token stay
// valid until they expire or are revoked.  Never rejects.
export const SERVER_SIGN_OUT_WAIT_MS = 2500
export const serverSignOutConfirmed = (r) =>
  !!r && typeof r === 'object' && (r.ok === true || r.status === 401 || r.skipped === true)
export async function endServerSession ({ end = logoutSession, waitMs = SERVER_SIGN_OUT_WAIT_MS } = {}) {
  let timer = null
  try {
    const call = Promise.resolve().then(() => (typeof end === 'function' ? end() : null))
    const cap = new Promise((resolve) => { timer = setTimeout(() => resolve(null), waitMs) })
    const result = await Promise.race([
      call.catch((err) => { console.error('sign-out: ending the server session failed', err); return null }),
      cap,
    ])
    return serverSignOutConfirmed(result)
  } catch (err) {
    console.error('sign-out: ending the server session failed', err)
    return false
  } finally {
    clearTimeout(timer)
  }
}

// The one-time notice after an unconfirmed sign-out.  logout() reloads the
// page at /login, so the flag rides in sessionStorage (this tab only) and the
// next AuthProvider shows it as a toast that stays until dismissed.  Never a
// token or any identity in it.
export const SIGN_OUT_UNCONFIRMED_KEY = 'lextrack_signout_unconfirmed'
export const SIGN_OUT_UNCONFIRMED_TITLE = 'Signed out on this computer only'
export const SIGN_OUT_UNCONFIRMED_MESSAGE =
  'Omnia could not confirm with the server that this sign-in ended (no answer, or a server error). ' +
  'A copy of it would keep working until it expires: 12 hours, or 30 days with "Keep me signed in". ' +
  'To end it now, sign in and click Revoke on that session under Profile → Active Sessions.'
function readUnconfirmedSignOutFlag () {
  try { return window.sessionStorage.getItem(SIGN_OUT_UNCONFIRMED_KEY) === '1' } catch { return false }
}
function UnconfirmedSignOutNotice () {
  const toast = useToast()
  const shown = useRef(false)
  useEffect(() => {
    try { window.sessionStorage.removeItem(SIGN_OUT_UNCONFIRMED_KEY) } catch { /* storage blocked */ }
    if (shown.current) return
    shown.current = true
    toast.warn(SIGN_OUT_UNCONFIRMED_MESSAGE, { title: SIGN_OUT_UNCONFIRMED_TITLE, duration: 0 })
  }, [toast])
  return null
}

// Sign-out with open timers (firm decisions 2026-10-14).  Timers live in this
// computer's storage and are filtered by the signed-in user, so they simply
// wait for the next sign-in — but signing out first says so, and offers to
// discard them.  Keeping them pauses the running one first (a timer must not
// run all night after someone leaves); its time is kept.  Only the signed-in
// person's own timers written by the new code (v2) are counted, paused or
// discarded; timers from before the update, and other people's, are never
// touched.
//
// The app's dialog has two buttons (useAsk), so the choice is:
//   1. "You have N open timers — they stay on this computer, paused, until
//      you sign back in"  [Discard them and sign out]  [Sign out]
//   2. Discard asks again (danger): [Cancel] stays signed in with nothing
//      changed; [Discard them and sign out] removes them, then signs out.
// Escape on the first dialog lands on the second, never on a discard.
//
// Returns true when the caller should go ahead and sign out.
export async function confirmSignOutWithTimers ({ ask, store = timerStore, uid = tokenUid() } = {}) {
  if (!uid || typeof ask !== 'function') return true
  let n = 0
  try { n = store.listMyTimers(uid).filter(r => r.v2).length } catch { n = 0 }
  if (n === 0) return true
  const keep = await ask({
    type: 'confirm',
    message: MESSAGES.signOut(n),
    confirmLabel: 'Sign out',
    cancelLabel: 'Discard them and sign out'
  })
  if (keep === true) {
    // Pause, then sign out.  A pause that fails (storage) is logged, never
    // allowed to block signing out.
    let rows = []
    // implicitlyPaused rows still say running in storage: pausing persists
    // their cut, so nothing restarts counting before the next sign-in.
    try { rows = store.listMyTimers(uid).filter(r => r.v2 && (r.ticking || r.implicitlyPaused)) } catch (err) { console.error('sign-out: listing timers failed', err) }
    for (const r of rows) {
      let p = null
      try { p = store.pause(r.key, { uid }) } catch (err) { console.error('sign-out: pausing a timer failed', err) }
      if (p && !p.ok) console.error('sign-out: a timer could not be paused', p.reason)
    }
    return true
  }
  const sure = await ask({
    type: 'confirm',
    danger: true,
    message: n === 1
      ? 'Discard your open timer? Its time will not be saved.'
      : `Discard all ${n} of your open timers? Their time will not be saved.`,
    confirmLabel: 'Discard them and sign out',
    cancelLabel: 'Cancel'
  })
  if (sure !== true) return false
  let r = null
  try { r = store.discardAllMine(uid) } catch (err) { console.error('sign-out: discarding timers failed', err) }
  if (r && !r.ok) console.error('sign-out: some timers could not be discarded', r.reason)
  return true
}

export const AuthProvider = ({ children }) => {
  // AuthProvider sits inside PromptProvider (App.js), so sign-out can ask.
  const ask = useAsk()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [firmTimezone, setFirmTimezone] = useState('America/Los_Angeles')
  // Read once at mount; UnconfirmedSignOutNotice clears it after showing.
  const [unconfirmedSignOut] = useState(readUnconfirmedSignOutFlag)

  useEffect(() => {
    // Pop-out window auth bridge: main window passes token via #auth=
    // hash fragment because the main window (default session) and
    // pop-out windows (persist:lextrack session) don't share localStorage.
    // Consume the hash BEFORE anything else so the rest of this effect
    // sees the token via getToken().  No-op when no hash present.
    consumeAuthHashIfPresent()
    // One-time migration for Electron desktop users: if their token is still
    // in sessionStorage from a pre-pop-out-window build, move it to
    // localStorage.
    migrateSessionTokenIfElectron()
    const token = getToken()
    if (token) {
      Promise.all([
        getMe(),
        api.get('/api/settings').catch(() => ({ data: {} }))
      ])
        .then(([userRes, settingsRes]) => {
          // Before the user is set: another person's Pinned / Recent lists
          // on this browser are emptied first (lib/quickListOwner.js), and
          // the zoom takes this person's Display size (lib/displayZoom.js).
          claimQuickListsFor(userRes.data?.id)
          claimDisplaySizeFor(userRes.data?.id)
          setUser(userRes.data)
          setSentryUser(userRes.data)
          if (settingsRes.data?.timezone) setFirmTimezone(settingsRes.data.timezone)
        })
        .catch(() => {
          clearToken()
          claimDisplaySizeFor(null)
          setUser(null)
          clearSentryUser()
        })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  // Shared post-login work: cache timezone + announce so other contexts re-hydrate.
  // The /login response only includes {id, email, role, name} for the user —
  // NOT permissions.  For non-admin users that means hasPermission() returns
  // false for everything until the next page refresh (which triggers getMe()).
  // Symptom: completely empty sidebar after a fresh login.  Fix: fetch the
  // full user via /me right after setting the token so permissions are
  // populated for the very first render.
  const finishLogin = async (data, remember) => {
    setToken(data.token, remember)
    let me = null
    try {
      const meRes = await getMe()
      me = meRes.data
      claimQuickListsFor(meRes.data?.id)
      claimDisplaySizeFor(meRes.data?.id)
      setUser(meRes.data)
      setSentryUser(meRes.data)
    } catch {
      // Fall back to the partial login payload if /me errors — better to have
      // a logged-in admin than a logged-out user, even if their sidebar is sparse.
      claimQuickListsFor(data.user?.id ?? tokenUid())
      claimDisplaySizeFor(data.user?.id ?? tokenUid())
      setUser(data.user)
      setSentryUser(data.user)
    }
    try {
      const settingsRes = await api.get('/api/settings')
      if (settingsRes.data?.timezone) setFirmTimezone(settingsRes.data.timezone)
    } catch (e) {}
    try { window.dispatchEvent(new Event('auth-login')) } catch {}
    // #830: every sign-in path (password, TOTP, passkey) gets the same
    // must_change_password answer — the passkey response never carried it, so
    // a flagged user signing in by passkey skipped the /profile redirect.
    if (data && data.must_change_password === undefined && me && typeof me.must_change_password === 'boolean') {
      return { ...data, must_change_password: me.must_change_password }
    }
    return data
  }

  // Re-read the signed-in user from /me — e.g. after changing the password on
  // the Profile page clears must_change_password server-side (#830), so the
  // banner and the /login guest guard stop acting on the stale cached value.
  // /me answers for the token stored NOW, which may be another person's (their
  // remembered sign-in in another tab; the timer bar re-reads after a sign-in
  // in the main window), so the quick lists and the Display size are claimed
  // before setUser here too, as on every path that sets the user.
  const refreshUser = async () => {
    try {
      const meRes = await getMe()
      claimQuickListsFor(meRes.data?.id)
      claimDisplaySizeFor(meRes.data?.id)
      setUser(meRes.data)
    } catch { /* keep the current user; next full load re-reads /me */ }
  }

  // Step 1: password. If the user has 2FA enabled, returns
  // { totp_required: true, totp_challenge_token } — caller must then call completeTotpLogin.
  // Otherwise finishes auth normally and returns { token, user, must_change_password }.
  const login = async (email, password, remember = false) => {
    const res = await apiLogin(email, password, remember)
    if (res.data.totp_required) return res.data  // hand off to caller; not authenticated yet
    return finishLogin(res.data, remember)
  }

  // Passkey login — server-side flow is a two-step challenge/response.
  // Passing through finishLogin so /me hydration happens exactly like the
  // password path.
  const completePasskeyLogin = async (data, remember = false) => {
    return finishLogin(data, remember)
  }

  // Step 2: verify a TOTP (or backup) code against the challenge token from step 1.
  const completeTotpLogin = async (challengeToken, code, remember = false) => {
    const res = await verifyTotp(challengeToken, code)
    return finishLogin(res.data, remember)
  }

  // Every sign-out entry point (the sidebar's Sign Out, Ctrl+K "Sign out")
  // calls this, so the open-timers question is asked once, here.  Then the
  // server session is ended while the token is still stored (the request
  // must carry it), and only then is this device's copy cleared.  Cancelling
  // the timers question ends nothing.
  const logout = async () => {
    let go = true
    try { go = await confirmSignOutWithTimers({ ask }) } catch (err) {
      console.error('sign-out: timer check failed', err)
      go = true
    }
    if (!go) return false
    const confirmed = await endServerSession()
    clearToken()
    // Signed out: Auto (lib/displayZoom.js); the person's choice stays kept.
    claimDisplaySizeFor(null)
    setUser(null)
    clearSentryUser()
    // Purge the singleton reminder cache so a shared / kiosk device doesn't
    // leak the previous user's dismissed-reminder state into the next session.
    try { resetReminderState() } catch (_) { /* best-effort */ }
    if (!confirmed) {
      try { window.sessionStorage.setItem(SIGN_OUT_UNCONFIRMED_KEY, '1') } catch { /* storage blocked: no notice */ }
    }
    window.location.href = '/login'
    return true
  }

  // Idle auto-logout was REMOVED per feedback #47 (Darian + Shawn).
  //
  // Original design (30 min → 60 min progression) was kicking attorneys out
  // mid-workflow when they minimized to tray for a meeting or worked in
  // another app for an extended stretch — and worst of all, it overrode
  // the "Keep me signed in" preference users explicitly opted into.
  //
  // The actual session backstop is the JWT expiry signed in auth.js:
  //   • 12h for normal logins
  //   • 30d when "Keep me signed in" is checked
  // Expired tokens hit the global verifyStaff 401 handler; the axios 401
  // interceptor clears storage and redirects to /login.  That's the real
  // security floor — the idle timer was a redundant layer that did more harm
  // than good for an installed-desktop-app, trusted-machine workflow.
  //
  // For a stolen-laptop scenario the mitigations remain: 2FA on login,
  // new-device email notifications, the rotate-JWT_SECRET kill-switch,
  // and the user explicitly choosing remember-me vs not.
  //
  // Also cleans up any stale lextrack_last_activity row left behind from
  // the previous idle-timer era so it doesn't grow forever.
  useEffect(() => {
    try { localStorage.removeItem('lextrack_last_activity') } catch {}
  }, [])

  return (
    <AuthContext.Provider value={{ user, login, completeTotpLogin, completePasskeyLogin, logout, loading, firmTimezone, refreshUser }}>
      {unconfirmedSignOut && <UnconfirmedSignOutNotice />}
      {!loading && children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
